/**
 * API Client Factory
 * ============================================================================
 *
 * Purpose:
 * --------
 * Creates and configures Axios clients used throughout the application
 * for communicating with backend services.
 *
 * This module is responsible for:
 * - Applying global request/response interceptors
 * - Enforcing consistent configuration (base URL, timeouts, headers)
 * - Providing specialized clients for standard requests and uploads
 *
 * Design principles:
 * ------------------
 * - All network requests must flow through a configured client.
 * - Interceptor behavior is centralized and applied consistently.
 * - Client instances are created once and reused.
 *
 * Non-goals:
 * ----------
 * - Making domain-specific API calls
 * - Performing business logic or state management
 * - Handling UI concerns
 *
 * Changes to this file affect all outgoing network traffic and
 * should be reviewed carefully.
 * ============================================================================
 */

import axios, { AxiosInstance } from 'axios';
import {
  handleOnRequestError,
  handleOnRequestSuccess,
  handleOnResponseError,
  handleOnResponseSuccess,
} from './handlers';

import { API_CONFIG } from './constants';

/**
 * Creates the primary API client instance.
 *
 * Applies standard interceptors for authentication, tracing,
 * response normalization, and error handling.
 */
const createApiClient = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: API_CONFIG.baseURL,
    timeout: API_CONFIG.timeout,
    headers: API_CONFIG.headers,
  });

  instance.interceptors.request.use(handleOnRequestSuccess, handleOnRequestError);
  instance.interceptors.response.use(handleOnResponseSuccess, handleOnResponseError);

  return instance;
};

/**
 * Shared API client used for the majority of application requests.
 */
export const apiClient = createApiClient();

/**
 * Creates a specialized API client for file uploads.
 *
 * Uses an extended timeout to accommodate large payloads
 * while preserving the same interceptor behavior as the
 * primary API client.
 */
export const createUploadClient = (): AxiosInstance => {
  const uploadClient = axios.create({
    baseURL: API_CONFIG.baseURL,
    timeout: API_CONFIG.uploadTimeout,
    // Don't set Content-Type header manually - axios will auto-detect FormData
    // and set the proper multipart/form-data header with boundary
  });

  uploadClient.interceptors.request.use(handleOnRequestSuccess, handleOnRequestError);
  uploadClient.interceptors.response.use(handleOnResponseSuccess, handleOnResponseError);

  return uploadClient;
};

/**
 * Shared upload client for multipart/form-data requests.
 */
export const uploadClient = createUploadClient();
